import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AdminNotifications from './AdminNotifications';

const Navigation = ({ currentDonor, adminUser, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home', icon: '🏠', public: true },
    { path: '/requests', label: 'Blood Requests', icon: '🩸', public: true },
    { path: '/stars', label: 'Leaderboard', icon: '⭐', public: true },
    { path: '/donor-registration', label: 'Become a Donor', icon: '❤️', public: true, hideIfLoggedIn: true },
    { path: '/request-blood', label: 'Request Blood', icon: '🆘', public: true },
    { path: '/my-requests', label: 'My Requests', icon: '📋', requiresAuth: true },
    { path: '/profile', label: 'My Profile', icon: '👤', requiresAuth: true },
    { path: '/admin', label: 'Admin', icon: '⚙️', requiresAdmin: true },
  ];

  const filteredLinks = navLinks.filter(link => {
    if (link.requiresAuth && !currentDonor) return false;
    if (link.requiresAdmin && !adminUser) return false;
    if (link.hideIfLoggedIn && (currentDonor || adminUser)) return false;
    return true;
  });

  // Only be transparent on the home page when NOT scrolled
  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;
  const isAdminPage = location.pathname === '/admin';

  if (isAdminPage) return null;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isTransparent ? '' : 'nav-glass shadow-lg'
        }`}
        style={isTransparent ? { background: 'rgba(255,255,255,0.0)' } : {}}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0">
              <div className="relative">
                <img 
                  src="/ocym-logo.png" 
                  alt="OCYM Logo" 
                  className="w-9 h-9 rounded-xl object-contain shadow-glow-sm group-hover:shadow-glow transition-all duration-300"
                />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-bold text-sm tracking-tight text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  OCYM Raktadhan
                </span>
                <span className={`hidden sm:block text-xs font-medium -mt-0.5 ${isTransparent ? 'text-white/60' : 'text-white/70'}`}>रक्तदान · Donors Network</span>
              </div>
            </Link>

            {/* Desktop Nav links */}
            <div className="hidden md:flex items-center gap-1">
              {filteredLinks
                .filter(l => !l.requiresAdmin)
                .map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${
                    isActive(link.path)
                      ? isTransparent
                        ? '!bg-white/20 !text-white font-semibold'
                        : 'nav-link-active'
                      : isTransparent
                        ? '!text-white/80 hover:!text-white hover:!bg-white/15'
                        : '!text-white/80 hover:!text-white hover:!bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {adminUser && (
                <Link
                  to="/admin"
                  className={`nav-link ${
                    isActive('/admin')
                      ? isTransparent
                        ? '!bg-white/20 !text-white font-semibold'
                        : 'nav-link-active'
                      : isTransparent
                        ? '!text-white/80 hover:!text-white hover:!bg-white/15'
                        : '!text-white/80 hover:!text-white hover:!bg-white/10'
                  }`}
                >
                  Admin
                </Link>
              )}
            </div>

            {/* Desktop User Area */}
            <div className="hidden md:flex items-center gap-3">
              {adminUser ? (
                <>
                  <AdminNotifications adminEmail={adminUser.email} />
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-900 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-crimson-400 animate-pulse-crimson"></span>
                    <span className="text-xs font-semibold text-surface-200 tracking-wide">ADMIN</span>
                  </div>
                  <span className="text-sm text-white/70 hidden lg:block max-w-36 truncate">{adminUser.email}</span>
                  <button
                    onClick={onLogout}
                    className="btn btn-ghost btn-sm !text-white/80 hover:!text-white hover:!bg-white/10"
                  >
                    Sign Out
                  </button>
                </>
              ) : currentDonor ? (
                <>
                  <Link to="/profile" className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-white/10 transition-all">
                    <div className="w-8 h-8 rounded-full bg-crimson-gradient flex items-center justify-center text-white text-xs font-bold">
                      {currentDonor.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="text-left hidden lg:block">
                      <div className="text-sm font-semibold leading-tight text-white">{currentDonor.name?.split(' ')[0]}</div>
                      <div className="text-xs font-medium text-white/70">{currentDonor.bloodGroup}</div>
                    </div>
                  </Link>
                  <button
                    onClick={onLogout}
                    className={`btn btn-ghost btn-sm ${isTransparent ? '!text-white/80 hover:!text-white hover:!bg-white/10' : '!text-white/80 hover:!text-white hover:!bg-white/10'}`}
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className={`btn btn-ghost btn-sm ${isTransparent ? '!text-white/90 hover:!text-white' : '!text-white/90 hover:!text-white hover:!bg-white/10'}`}>
                    Sign In
                  </Link>
                  <Link to="/donor-registration" className="btn btn-primary btn-sm">
                    Join Now
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden p-2 rounded-xl transition-all ${isTransparent ? 'text-white hover:bg-white/10' : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900'}`}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Donor Status Bar */}
        {currentDonor && (
          <div className="hidden sm:block border-t border-crimson-100 bg-crimson-50/60 backdrop-blur-sm px-4 py-1.5">
            <div className="max-w-7xl mx-auto flex items-center justify-between text-xs">
              <div className="flex items-center gap-4 text-surface-600">
                <span>Blood Group: <strong className="text-crimson-700">{currentDonor.bloodGroup}</strong></span>
                <span className="hidden sm:inline">📍 {currentDonor.location}, {currentDonor.district}</span>
              </div>
              <div className={`status-pill ${currentDonor.isAvailable ? 'status-available' : 'status-unavailable'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${currentDonor.isAvailable ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                {currentDonor.isAvailable ? 'Available to Donate' : 'Currently Unavailable'}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Menu Drawer */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setIsMenuOpen(false)}>
          <div className="absolute inset-0 bg-surface-950/40 backdrop-blur-sm" />
          <div
            className="absolute top-0 right-0 bottom-0 w-72 bg-white shadow-2xl animate-slideDown"
            onClick={(e) => e.stopPropagation()}
            style={{ animationDuration: '300ms' }}
          >
            <div className="flex items-center justify-between p-5 border-b border-surface-100">
              <div className="flex items-center gap-2">
                <img 
                  src="/ocym-logo.png" 
                  alt="OCYM Logo" 
                  className="w-8 h-8 rounded-xl object-contain"
                />
                <div>
                  <div className="font-bold text-surface-900 text-sm">OCYM Raktadhan</div>
                  <div className="text-xs text-crimson-500 font-medium" style={{ fontFamily: 'Noto Sans Devanagari, sans-serif' }}>रक्तदान</div>
                </div>
              </div>
              <button onClick={() => setIsMenuOpen(false)} className="p-1.5 rounded-lg text-surface-400 hover:text-surface-600 hover:bg-surface-100">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* User info */}
            {currentDonor && (
              <div className="p-4 bg-crimson-50 border-b border-crimson-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-crimson-gradient flex items-center justify-center text-white font-bold">
                    {currentDonor.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-surface-800">{currentDonor.name}</div>
                    <div className="text-xs text-crimson-600 font-medium">{currentDonor.bloodGroup} · {currentDonor.district}</div>
                  </div>
                </div>
              </div>
            )}

            {adminUser && (
              <div className="p-4 bg-surface-900 border-b border-surface-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-surface-700 flex items-center justify-center">
                    <span>⚙️</span>
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">Admin</div>
                    <div className="text-xs text-surface-400 truncate max-w-40">{adminUser.email}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Nav Links */}
            <div className="p-4 space-y-1 flex-1">
              {filteredLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive(link.path)
                      ? 'bg-crimson-50 text-crimson-700 font-semibold'
                      : 'text-surface-600 hover:bg-surface-50 hover:text-surface-900'
                  }`}
                >
                  <span className="text-base">{link.icon}</span>
                  {link.label}
                  {isActive(link.path) && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-crimson-500"></span>
                  )}
                </Link>
              ))}
            </div>

            {/* Mobile Auth Buttons */}
            <div className="p-4 border-t border-surface-100 space-y-2">
              {(currentDonor || adminUser) ? (
                <button
                  onClick={() => { onLogout(); setIsMenuOpen(false); }}
                  className="btn btn-outline btn-md w-full"
                >
                  Sign Out
                </button>
              ) : (
                <>
                  <Link to="/login" className="btn btn-ghost btn-md w-full border border-surface-200">
                    Sign In
                  </Link>
                  <Link to="/donor-registration" className="btn btn-primary btn-md w-full">
                    Join as Donor
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
